import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { Field, InlineAlert, type useToast } from "../company-ui";
import "./templates.css";

/**
 * The band editor (ALN-35, docs/pages/templates.md B "Band editor", C "Edit sizes" and "Type a size's
 * hours"; mockup v26 and v31): an activity's unit, where Small and Medium end, whether hours are for the
 * whole job or each month, and, optionally, each size's hours typed by hand with a reason. Saving
 * changed sizes writes a new version; typed hours alone don't.
 */
type Size = "S" | "M" | "L";
type Unit = "tons" | "gallons" | "cases" | "acres";
type Per = "job" | "month";
interface SizeHours { hours: number | null; source: "median" | "typed" | null; setBy?: string; setAt?: string }
interface BandsBody {
  activity: { key: string; name: string; stage: string };
  current: { version: number; unit: Unit; hoursPer: Per; smallMax: number; mediumMax: number } | null;
  medians: Record<Size, number | null>;
  bandHours: Record<Size, SizeHours>;
}

const SIZES: Size[] = ["S", "M", "L"];
const SIZE_NAME: Record<Size, string> = { S: "Small", M: "Medium", L: "Large" };
const UNITS: Unit[] = ["tons", "gallons", "cases", "acres"];
const HOUR_CARDS = "0.5, 1, 2, 3, 5, 8, 13, 20";
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Check your connection and try again.");
const day = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const fmt = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 3 });
const fmt1 = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** "up to 5 tons", "5 to 20 tons", "over 20 tons", from the thresholds as typed. */
function range(z: Size, small: number, medium: number, unit: Unit) {
  const ok = small > 0 && medium > small;
  if (z === "S") return small > 0 ? `up to ${fmt(small)} ${unit}` : unit;
  if (z === "M") return ok ? `${fmt(small)} to ${fmt(medium)} ${unit}` : unit;
  return ok ? `over ${fmt(medium)} ${unit}` : unit;
}
function sourceLine(h: SizeHours | undefined, median: number | null) {
  if (h?.source === "typed" && h.setBy && h.setAt) return `Entered by hand by ${h.setBy}, ${day(h.setAt)}`;
  if (median != null) return `Team estimate, median ${fmt1(median)} h`;
  return "None yet";
}
/** The typed hours' first problem, in the packet's words; empty clears a size. */
function hoursProblem(typed: Record<Size, string>, changed: Size[], why: string): { at: Size | "why"; msg: string } | null {
  for (const z of changed) {
    const s = typed[z].trim();
    if (s === "") continue;
    const v = Number(s);
    if (!Number.isFinite(v) || v < 0) return { at: z, msg: "Hours should be a number of zero or more." };
    if (v >= 10_000) return { at: z, msg: "Enter hours under 10,000." };
  }
  if (changed.length && !why.trim()) return { at: "why", msg: "Say why you're typing these hours." };
  return null;
}

export function BandEditor({ activityKey, onClose: close, onSaved, toast }: {
  activityKey: string; onClose: () => void; onSaved?: () => void;
  toast: ReturnType<typeof useToast>;
}) {
  const qc = useQueryClient();
  // closing hands focus back to the control that opened the editor
  const opener = useRef(document.activeElement as HTMLElement | null);
  const onClose = () => { close(); const o = opener.current; if (o?.isConnected) requestAnimationFrame(() => o.focus()); };
  const key = ["activity-bands", activityKey];
  const q = useQuery({ queryKey: key, queryFn: () => api<BandsBody>(`/activities/${encodeURIComponent(activityKey)}/bands`) });
  const headingId = useId();
  const unitRef = useRef<HTMLSelectElement>(null);
  const [unit, setUnit] = useState<Unit | "">("");
  const [small, setSmall] = useState("");
  const [medium, setMedium] = useState("");
  const [per, setPer] = useState<Per>("job");
  const [typed, setTyped] = useState<Record<Size, string>>({ S: "", M: "", L: "" });
  const [why, setWhy] = useState("");
  const [sizeErr, setSizeErr] = useState<{ at: "small" | "medium" | null; msg: string } | null>(null);
  const [hoursErr, setHoursErr] = useState<{ at: Size | "why"; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // the form starts from the current sizes and typed hours, and again after someone else saved first
  const loaded = q.data;
  useEffect(() => {
    if (!loaded) return;
    const c = loaded.current;
    setUnit(c?.unit ?? "tons"); setSmall(c ? String(c.smallMax) : ""); setMedium(c ? String(c.mediumMax) : ""); setPer(c?.hoursPer ?? "job");
    setTyped({ S: hoursText(loaded, "S"), M: hoursText(loaded, "M"), L: hoursText(loaded, "L") });
    unitRef.current?.focus();
  }, [loaded]);

  if (q.isPending) return <div className="card bandedit" aria-busy="true" aria-label="Loading the sizes" />;
  if (q.isError || !loaded) {
    return (
      <div className="card bandedit">
        <InlineAlert variant="warning" title="The sizes couldn't load. Try again in a minute; nothing was changed.">
          <div className="actions"><button type="button" className="btn" onClick={() => void q.refetch()}>Retry</button><button type="button" className="btn" onClick={onClose}>Close</button></div>
        </InlineAlert>
      </div>
    );
  }
  const name = loaded.activity.name;
  const sm = Number(small), md = Number(medium);
  const u = (unit || "tons") as Unit;

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSizeErr(null); setHoursErr(null);
    const sErr: { at: "small" | "medium" | null; msg: string } | null = !unit ? { at: null, msg: "Choose a unit." }
      : !(small.trim() !== "" && sm > 0) ? { at: "small", msg: "Enter where Small ends, a number above zero." }
      : !(md > sm) ? { at: "medium", msg: "Medium must end above where Small ends." } : null;
    if (sErr) { setSizeErr(sErr); if (sErr.at) document.getElementById(`${headingId}-${sErr.at}`)?.focus(); return; }
    const c = loaded.current;
    const sizesChanged = !c || c.unit !== unit || c.smallMax !== sm || c.mediumMax !== md || c.hoursPer !== per;
    // a new size version starts with no typed hours, so only the fields someone changed are saved on it
    const changed = SIZES.filter(z => typed[z].trim() !== hoursText(loaded, z));
    const hErr = hoursProblem(typed, changed, why);
    if (hErr) { setHoursErr(hErr); document.getElementById(hErr.at === "why" ? `${headingId}-why` : `${headingId}-h-${hErr.at}`)?.focus(); return; }
    if (!sizesChanged && !changed.length) { onClose(); return; }
    setBusy(true);
    try {
      // one request, saved together or not at all: new sizes carry the changed hours with them
      const hours = changed.length ? { sizes: Object.fromEntries(changed.map(z => [z, typed[z].trim() === "" ? null : typed[z].trim()])), reason: why.trim() } : {};
      let version = c?.version ?? 0;
      if (sizesChanged) {
        const r = await api<{ version: number }>(`/activities/${encodeURIComponent(activityKey)}/bands`, { method: "PUT", body: { expectedVersion: version, unit, smallMax: sm, mediumMax: md, hoursPer: per, ...hours } });
        version = r.version;
      } else {
        await api(`/activities/${encodeURIComponent(activityKey)}/bands/${version}/hours`, { method: "PUT", body: hours });
      }
      void qc.invalidateQueries({ queryKey: key });
      toast(sizesChanged ? `Sizes saved for ${name} as version ${version}. Plans and templates keep the sizes they have.` : `Hours saved for ${name}.`);
      onSaved?.();
      onClose();
    } catch (err) {
      // someone saved first: show their sizes and keep this person's typing out of a stale version
      if (err instanceof ApiError && err.code === "edit_conflict") void q.refetch();
      setSizeErr({ at: null, msg: message(err) });
    } finally { setBusy(false); }
  };

  return (
    <form className="card bandedit" role="dialog" aria-labelledby={headingId} noValidate onSubmit={e => void submit(e)}
      onKeyDown={e => { if (e.key === "Escape") onClose(); }}>
      <div className="card__h"><div>
        <h2 className="card__t" id={headingId}>Sizes for {name}</h2>
        <p className="card__d">Small, Medium and Large by volume. The size is picked from each program's volume, and each size gets its own team estimate.</p>
      </div></div>
      <div className="fields bandedit__sizes">
        <Field label="Unit">
          <select ref={unitRef} className="input" value={unit} onChange={e => setUnit(e.target.value as Unit)}>
            {UNITS.map(x => <option key={x} value={x}>{x[0]!.toUpperCase() + x.slice(1)}</option>)}
          </select>
        </Field>
        <Field label="Small ends at" error={sizeErr?.at === "small" ? sizeErr.msg : null}><input id={`${headingId}-small`} className="input tnum" inputMode="decimal" value={small} onChange={e => setSmall(e.target.value)} /></Field>
        <Field label="Medium ends at" error={sizeErr?.at === "medium" ? sizeErr.msg : null}><input id={`${headingId}-medium`} className="input tnum" inputMode="decimal" value={medium} onChange={e => setMedium(e.target.value)} /></Field>
        <fieldset className="bandedit__per">
          <legend className="field__label">Hours are for</legend>
          <label><input type="radio" name={`${headingId}-per`} checked={per === "job"} onChange={() => setPer("job")} /> The whole job</label>
          <label><input type="radio" name={`${headingId}-per`} checked={per === "month"} onChange={() => setPer("month")} /> Each month</label>
        </fieldset>
      </div>
      {sizeErr && !sizeErr.at && <p className="field__error bandedit__err" role="alert">{sizeErr.msg}</p>}
      <fieldset className="bandedit__hours" aria-describedby={`${headingId}-hint`}>
        <legend className="card__t">Hours for each size (optional)</legend>
        <p className="small muted m0" id={`${headingId}-hint`}>If you already know how long this work takes, type it here. It's used like a team estimate until a survey's medians are applied. Saving typed hours doesn't start a new version of the sizes.</p>
        <p className="small m0"><b>{per === "month" ? "Hours each month" : "Hours for the whole job"}</b></p>
        <div className="fields">
          {SIZES.map(z => (
            <div className="field" key={z}>
              <label className="field__label" htmlFor={`${headingId}-h-${z}`}>{SIZE_NAME[z]} <span className="muted">({range(z, sm, md, u)})</span></label>
              <span className="bandedit__unit">
                <input id={`${headingId}-h-${z}`} className="input tnum" inputMode="decimal" value={typed[z]} aria-invalid={hoursErr?.at === z || undefined}
                  aria-describedby={`${headingId}-src-${z}${hoursErr?.at === z ? ` ${headingId}-herr` : ""}`} onChange={e => setTyped(t => ({ ...t, [z]: e.target.value }))} />
                <span aria-hidden="true">h</span>
              </span>
              <small className="field__hint" id={`${headingId}-src-${z}`}>{sourceLine(loaded.bandHours[z], loaded.medians[z])}</small>
            </div>
          ))}
        </div>
        <div className="field">
          <label className="field__label" htmlFor={`${headingId}-why`}>Why you're typing these</label>
          <input id={`${headingId}-why`} className="input" maxLength={200} aria-required="true" value={why} aria-invalid={hoursErr?.at === "why" || undefined}
            aria-describedby={hoursErr?.at === "why" ? `${headingId}-herr` : undefined} onChange={e => setWhy(e.target.value)} />
        </div>
        {hoursErr && <p className="field__error" id={`${headingId}-herr`} role="alert">{hoursErr.msg}</p>}
      </fieldset>
      <div className="card__c stackgap stackgap--sm">
        <p className="small muted m0">People pick from the same hour cards for every size: {HOUR_CARDS} hours, Other or Not sure.</p>
        <InlineAlert variant="warning" title="Saving starts a new version of these sizes. Plans and templates keep the sizes and hours they have. Medians from earlier surveys won't apply to the new sizes; send a new survey for this activity." />
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy || undefined}>{busy ? "Saving…" : "Save sizes"}</button>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </form>
  );
}

function hoursText(b: BandsBody, z: Size) {
  const h = b.bandHours[z];
  return h?.source === "typed" && h.hours != null ? String(h.hours) : "";
}
